import { core } from '@icc/shared';
import { isArray, isDefined, isObject } from '@icc/shared/helpers/b2b';
import { SalesDocumentType } from '../sales-document-types';
import { DocumentNumberToken } from '../models/document-numbering.model';
import { SalesDocument } from '../models/sales-document.model';
import {
    DocumentNumberElement,
    NumberingParams,
    tokenGenerators,
} from './document-numbering-tokens';
import { IccDocument } from '../models/icc-document';

export class DocumentNumberingService {
    static generate(
        scheme: string,
        params: NumberingParams
    ): {
        key: (DocumentNumberToken | string)[];
        num?: number;
    } {
        if (!scheme) {
            return {
                key: [DocumentNumberToken.N_ALL],
            };
        }

        params.creationDate = DocumentNumberingService.getCreationDate(params);
        params.applicationNumber = DocumentNumberingService.getApplicationNumber(params);

        const elems = DocumentNumberingService.parseScheme(scheme);
        const key = [];
        let elem = '';
        let num;
        for (let i = 0; i < elems.length; i++) {
            if (!elems[i].b2cOfferSymbol || params.fromB2C) {
                if (isObject(params.def) && params.def[elems[i].type]) {
                    const parsedDef = core.parseJson(params.def[elems[i].type], true);
                    elem = isArray(parsedDef) ? parsedDef.join('') : params.def[elems[i].type];
                } else if (tokenGenerators[elems[i].type]) {
                    elem = tokenGenerators[elems[i].type](params, elems[i]);
                } else {
                    elem = elems[i].type;
                }
            }

            if (elems[i].type.startsWith('N_')) {
                num = elem;
            }

            elem = DocumentNumberingService.formatElement(elem, elems[i]);
            if (elem === '' && elems[i].removePrev && isDefined(key[i - 1])) {
                key[i - 1] = '';
            }

            key.push(elem);
            elem = '';
        }

        return { key, num };
    }

    private static getCreationDate(params: NumberingParams) {
        if (params.rootDocumentCreationDate) {
            return params.rootDocumentCreationDate;
        } else if (params.creationDate) {
            return params.creationDate;
        } else {
            return new Date();
        }
    }

    private static formatElement(elem: string, element: DocumentNumberElement) {
        elem = core.zeroPad(elem, element.pad);
        elem = element.empty && !elem ? element.empty : elem;
        elem = elem.toUpperCase();
        return elem;
    }

    private static getApplicationNumber(params: NumberingParams) {
        let applicationNumber = '';
        if (!core.isNode()) {
            applicationNumber = core.parseJson(localStorage.getItem('applicationNumber'));
            applicationNumber = applicationNumber[params.userId];
        }
        return applicationNumber;
    }

    /**
     * Tworzy tablice elementow skladowych kodu oferty
     * @param  {string} scheme schemat
     * @return {array}         elementy schematu
     */
    static parseScheme(scheme: string): DocumentNumberElement[] {
        return scheme.match(/(\{[^}]*\}|[^{]+)/gi).map((e) => ({
            type: (e.charAt(0) === '{'
                ? e.replace('{', '').replace('}', '').split(':')[0]
                : 'STRING') as DocumentNumberToken,
            value: e.charAt(0) === '{' ? e.replace('{', '').replace('}', '') : e,
            pad: ~~e.replace('{', '').replace('}', '').split(':')[1],
            empty: e.replace('{', '').replace('}', '').split(':')[2] || null,
            removePrev: e.replace('{', '').replace('}', '').split(':')[3] === '1',
            b2cOfferSymbol: e.replace('{', '').replace('}', '').split(':')[4] === '1',
        }));
    }


    static getNumberingScheme(document: IccDocument, documentType?: SalesDocument['salesDocumentTypeId']) {
        if (documentType || document.type === 'sales') {
            documentType = documentType ?? (document?.type === 'sales' && document.salesDocumentTypeId);
            if (documentType === SalesDocumentType.Order) {
                return 'orderScheme';
            }
            if (documentType === SalesDocumentType.Inquiry) {
                return 'inquiryScheme';
            }
            if (documentType === SalesDocumentType.ServiceOrder) {
                return 'serviceOrderScheme';
            }
            return 'quotationScheme';
        } else {
            return 'complaintScheme';
        }
    }

    static getElementInDocumentNumber(num: string[] | string, code: DocumentNumberToken, scheme: string) {
        if (!isArray(num)) {
            num = core.parseJson(num);
        }
        const index = this.parseScheme(scheme).map((e, i) => e.type === code ? i : null).filter(e => e !== null)[0];
        return num[index] || null;
    }
}
